import { z } from "zod";
import { scopeLabViewSchema } from "./model-labs.ts";

/**
 * Model health (openspec change `model-picking`, D6 to D8): what a probe of one connection ×
 * model × geography found, how often probes run, and the reasons a scope's resolved version
 * changes. Shared by the probe worker, the runtimes' resolution and the admin API.
 */

/**
 * A probe's outcome (D6): `ok` and `throttled` are healthy; `denied`, `unavailable` and `error`
 * are failures; `unknown` is no observation (#423): the probe could not reach the connection
 * itself (its role not assumable by the probe), so it says nothing of the versions, neither
 * healthy nor failing, and vouches for none of them. `retired` (#427) is a failure the provider
 * made permanent: Bedrock reports the model version reached its end of life. It never goes stale
 * and the scheduled tiers never probe it again (a full run does: deploy, "Check now", "Refresh
 * models").
 */
export const MODEL_HEALTH_STATUSES = [
  "ok",
  "throttled",
  "denied",
  "unavailable",
  "error",
  "unknown",
  "retired",
] as const;
export const modelHealthStatusSchema = z.enum(MODEL_HEALTH_STATUSES);
export type ModelHealthStatus = z.infer<typeof modelHealthStatusSchema>;

/** A version answers on a connection when its last probe there was `ok` or `throttled`. */
export function healthyStatus(status: ModelHealthStatus): boolean {
  return status === "ok" || status === "throttled";
}

/**
 * Why a connection's health is `unknown` (#423): `assume_denied`, its role refuses the probe's
 * role (a trust policy from before the probe role); `assume_failed`, assuming it failed
 * otherwise. A fixed code: never an AWS message (it may name the role or the account).
 */
export const MODEL_HEALTH_UNKNOWN_REASONS = ["assume_denied", "assume_failed"] as const;
export const modelHealthUnknownReasonSchema = z.enum(MODEL_HEALTH_UNKNOWN_REASONS);
export type ModelHealthUnknownReason = z.infer<typeof modelHealthUnknownReasonSchema>;

/**
 * What a connection's discovery found for a catalogued route (model-picking D6, slice 4b): the
 * lifecycle Bedrock reports for its underlying model (`ListFoundationModels`): `active`, or
 * `legacy` (still callable, being retired by the provider).
 */
export const MODEL_OFFERING_LIFECYCLES = ["active", "legacy"] as const;
export type ModelOfferingLifecycle = (typeof MODEL_OFFERING_LIFECYCLES)[number];

/**
 * Why a connection's listing is unknown (`listed` null): `list_denied`, its role refuses the
 * Bedrock list calls (`bedrock:ListInferenceProfiles`, `bedrock:ListFoundationModels`). A fixed
 * code: never an AWS message. Such a connection keeps the versions last probed ok on it but
 * vouches for none as healthy (nothing adopted through it) until fixed.
 */
export const MODEL_OFFERING_REASONS = ["list_denied"] as const;
export type ModelOfferingReason = (typeof MODEL_OFFERING_REASONS)[number];

/** One connection's "Refresh models" at most this often (D6): the "Check now" cooldown. */
export const REFRESH_MODELS_COOLDOWN_MS = 5 * 60_000;

/** `models.healthProbeInterval` (D6, Q1): hourly by default, daily allowed. */
export const HEALTH_PROBE_INTERVALS = ["hourly", "daily"] as const;
export const healthProbeIntervalSchema = z.enum(HEALTH_PROBE_INTERVALS);
export type HealthProbeInterval = z.infer<typeof healthProbeIntervalSchema>;

export const HEALTH_PROBE_INTERVAL_MS: Readonly<Record<HealthProbeInterval, number>> = {
  hourly: 60 * 60_000,
  daily: 24 * 60 * 60_000,
};

/** Health older than this many intervals is stale: treated as unknown, and it alarms (D6). */
export const HEALTH_STALE_PERIODS = 3;

/** One connection's "Check now" at most this often (D6). */
export const HEALTH_CHECK_NOW_COOLDOWN_MS = 5 * 60_000;

/** Why a scope's resolved version of a family changed (D8): `model.version_switched`. */
export const VERSION_SWITCH_REASONS = [
  "new_version_healthy",
  "version_unhealthy",
  "pin_unhealthy",
  "pin_restored",
  "admin_pin",
  "admin_unpin",
  "provider_changed",
  "policy_changed",
] as const;
export const versionSwitchReasonSchema = z.enum(VERSION_SWITCH_REASONS);
export type VersionSwitchReason = z.infer<typeof versionSwitchReasonSchema>;

/**
 * How a family resolved in a scope and geography: the newest healthy version (`latest`), the
 * scope's pin (`pinned`), a pin that is not healthy and so not honoured (`pin_unhonoured`), a
 * version kept while health is unknown (`kept`), or nothing usable (`none`).
 */
export const FAMILY_RESOLUTIONS = ["latest", "pinned", "pin_unhonoured", "kept", "none"] as const;
export type FamilyResolution = (typeof FAMILY_RESOLUTIONS)[number];

/** A version's health in a context, across the provider's connections (D6). */
export type VersionHealth = "healthy" | "unknown" | "unhealthy";

/** What the resolver reads of one version. */
export interface VersionState {
  readonly key: string;
  readonly health: VersionHealth;
  /** May be the family's newest version (not alpha or beta, not deprecated: D3). */
  readonly latestEligible: boolean;
}

/**
 * The version a family resolves to (D6, D7), from its versions in candidate order (newest
 * first, already filtered by the scope's policy: a pin never widens it):
 *
 * - a pin is honoured while its version is healthy, or while no version of the family is known
 *   healthy and the pin is not known unhealthy (no health yet: nothing to prefer over it);
 *   otherwise the family falls back below, and says `pin_unhonoured`;
 * - else the newest latest-eligible healthy version (a never-probed new version is not);
 * - else the version resolved before (`prior`), while its health is unknown, so a stalled probe
 *   job never removes a working model;
 * - else, still the version resolved before when no version is healthy (#401: a probe outage
 *   that marks every version denied moves no scope; resolution `none`, nothing is available);
 * - else the first version whose health is unknown (nothing ever resolved: catalogue order);
 * - else the first unhealthy one (still tried when nothing else answers); none without versions.
 */
export function resolveFamilyVersion(
  versions: readonly VersionState[],
  options: { pin?: string | undefined; prior?: string | undefined } = {},
): { key: string | undefined; resolution: FamilyResolution } {
  const anyHealthy = versions.some((v) => v.health === "healthy");
  const pinned =
    options.pin === undefined ? undefined : versions.find((v) => v.key === options.pin);
  if (pinned && (pinned.health === "healthy" || (!anyHealthy && pinned.health === "unknown"))) {
    return { key: pinned.key, resolution: "pinned" };
  }
  const unhonoured = options.pin !== undefined;
  const pick = (key: string, resolution: FamilyResolution) => ({
    key,
    resolution: unhonoured ? ("pin_unhonoured" as const) : resolution,
  });
  const latest = versions.find((v) => v.health === "healthy" && v.latestEligible);
  if (latest) {
    return pick(latest.key, "latest");
  }
  const healthy = versions.find((v) => v.health === "healthy");
  if (healthy) {
    return pick(healthy.key, "latest");
  }
  const prior = versions.find((v) => v.key === options.prior && v.health === "unknown");
  if (prior) {
    return pick(prior.key, "kept");
  }
  const kept = versions.find((v) => v.key === options.prior);
  if (kept) {
    return pick(kept.key, "none");
  }
  const unknown = versions.find((v) => v.health === "unknown");
  if (unknown) {
    return pick(unknown.key, "kept");
  }
  const [first] = versions;
  return first ? pick(first.key, "none") : { key: undefined, resolution: "none" };
}

/**
 * The versions in the order a turn tries them (D6): the resolved one first, then the other
 * healthy ones, then unknown, then unhealthy ones last (kept, so a turn can still try them),
 * each group in the given order.
 */
export function healthOrder(
  versions: readonly VersionState[],
  resolved: string | undefined,
): string[] {
  const rank = (v: VersionState) =>
    v.key === resolved ? 0 : v.health === "healthy" ? 1 : v.health === "unknown" ? 2 : 3;
  return versions
    .map((v, i) => ({ v, i }))
    .sort((a, b) => rank(a.v) - rank(b.v) || a.i - b.i)
    .map(({ v }) => v.key);
}

/**
 * The switch reason when a scope's resolved version changes from `from` to `to` (D8); undefined
 * when nothing changed. Admin pin writes record their own reasons (`admin_pin`, `admin_unpin`).
 */
export function switchReason(
  before: { key: string | undefined; resolution: FamilyResolution | undefined },
  after: { key: string | undefined; resolution: FamilyResolution },
  newer: (a: string, b: string) => boolean,
): VersionSwitchReason | undefined {
  if (before.key === after.key) {
    return undefined;
  }
  if (after.resolution === "pinned") {
    return "pin_restored";
  }
  if (after.resolution === "pin_unhonoured" && before.resolution === "pinned") {
    return "pin_unhealthy";
  }
  // Only a healthy version is adopted as newer (#401): moving to one kept while unknown or
  // resolved with nothing healthy (`none`) is never `new_version_healthy`.
  if (
    after.resolution !== "none" &&
    after.resolution !== "kept" &&
    before.key !== undefined &&
    after.key !== undefined &&
    newer(after.key, before.key)
  ) {
    return "new_version_healthy";
  }
  return "version_unhealthy";
}

/** One version's health in a scope, as admins see it (no account IDs: D6, D10). */
export const versionHealthViewSchema = z.object({
  model: z.string(),
  displayName: z.string(),
  geography: z.string(),
  /**
   * The best status across the provider's connections; `unknown` when no connection observed it
   * and one could not be reached (#423, see `reason`); null: never probed there.
   */
  status: modelHealthStatusSchema.nullable(),
  /** Why the status is `unknown` (a fixed code); null otherwise. */
  reason: modelHealthUnknownReasonSchema.nullable(),
  probedAt: z.string().nullable(),
  /** Since when it has failed on every connection; null while it answers somewhere. */
  failingSince: z.string().nullable(),
  stale: z.boolean(),
  /**
   * Whether a connection of the provider lists it there (slice 4b discovery): null when no
   * connection was discovered yet or its list calls were refused. Optional for one release.
   */
  listed: z.boolean().nullable().optional(),
});
export type VersionHealthView = z.infer<typeof versionHealthViewSchema>;

/** A family's resolution in a scope and geography (the Models screen's Efforts and Pins). */
export const familyResolutionViewSchema = z.object({
  family: z.string(),
  geography: z.string(),
  provider: z.string(),
  model: z.string().nullable(),
  resolution: z.enum(FAMILY_RESOLUTIONS),
  pin: z.string().nullable(),
  versions: z.array(versionHealthViewSchema),
});
export type FamilyResolutionView = z.infer<typeof familyResolutionViewSchema>;

/** `GET …/model-health`: the scope's families, versions and their health. */
export const scopeModelHealthSchema = z.object({
  families: z.array(familyResolutionViewSchema),
  /** The instance's probe interval (`models.healthProbeInterval`). */
  interval: healthProbeIntervalSchema,
  /** Seconds before "Check now" may run again; 0: now. */
  checkNowAfterSeconds: z.number().int().min(0),
  /** The labs the scope may offer, with their state (slice 4c); optional for one release. */
  labs: z.array(scopeLabViewSchema).optional(),
});
export type ScopeModelHealth = z.infer<typeof scopeModelHealthSchema>;

/** One `model.version_switched` event, as the scope's history lists it (D8). */
export const versionSwitchViewSchema = z.object({
  at: z.string(),
  family: z.string(),
  geography: z.string(),
  from: z.string().nullable(),
  to: z.string().nullable(),
  reason: versionSwitchReasonSchema,
  provider: z.string(),
  /** An admin's pin write; null for the system. */
  actor: z.enum(["system", "admin"]),
});
export type VersionSwitchView = z.infer<typeof versionSwitchViewSchema>;

export const versionSwitchHistorySchema = z.object({ switches: z.array(versionSwitchViewSchema) });
export type VersionSwitchHistory = z.infer<typeof versionSwitchHistorySchema>;
